/**
 * `<` encoded as: literal entity `&lt;`, double-encoded `&amp;lt;`, decimal `&#60;`,
 * hex `&#x3c;`, etc. — before structural tags (HTML section often has zero raw `<`).
 */
export const ENTITY_ENCODED_HTML_OPEN =
  /(?:&amp;lt;|&lt;|&amp;#60;|&amp;#x[\da-f]+;|&#60;|&#x[\da-f]+;|&#\d{1,8};)\s*(?:table|style|html|head|body|meta|center|blockquote|font|link|div|span|p|section|article|a|img)\b/i;

function codePointToChar(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) {
    return "\ufffd";
  }
  try {
    return String.fromCodePoint(code);
  } catch {
    return "\ufffd";
  }
}

/**
 * Decode common entities in HTML email fragments (iterative so `&amp;lt;` → `<`).
 * Narrow scope intentional—full WHATWG parsing belongs server-side or via `he` if added later.
 */
export function decodeHtmlEntitiesBasic(chunk: string): string {
  let s = chunk;
  for (let pass = 0; pass < 12; pass++) {
    const next = s
      .replace(/&#x([\da-f]+);/gi, (_, hex: string) => codePointToChar(Number.parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, num: string) => codePointToChar(Number.parseInt(num, 10)))
      .replace(/&quot;/gi, "\"")
      .replace(/&apos;/gi, "'")
      .replace(/&#39;/g, "'")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&nbsp;/gi, "\u00a0")
      .replace(/&amp;/gi, "&");
    if (next === s) break;
    s = next;
  }
  return s;
}

export function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Synced bodies sometimes store the HTML part HTML-entity–encoded (no literal `<` tags). */
export function hasEntityEncodedHtmlMarkup(content: string): boolean {
  return ENTITY_ENCODED_HTML_OPEN.test(content);
}

/**
 * Plain prefix + entity-encoded HTML tail (common when plain and HTML are concatenated).
 * Only decodes from the first entity-encoded structural tag onward so plaintext stays intact.
 */
export function normalizeEntityEncodedEmailHtml(body: string): string {
  if (!body || !hasEntityEncodedHtmlMarkup(body)) {
    return body;
  }
  const match = body.match(ENTITY_ENCODED_HTML_OPEN);
  if (!match || match.index === undefined) {
    return body;
  }
  const prefix = body.slice(0, match.index);
  const suffix = body.slice(match.index);
  return prefix + decodeHtmlEntitiesBasic(suffix);
}
